import React, { useState, useEffect } from "react";
import styles from "./editUserData.module.css";
import { getUserActionsEdit } from "../../utils/user";
import BackArrowButton from "../backArrowButton/backArrowButton";
import useMediaQuery from "../../hooks/useMediaQuery";
import { mobileWidth } from "../../utils/constants";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import { IUserReport } from "../../services/types/users.types";
import ActionReport from "../actionReport/actionReport";
import TreatmentPlan from "../treatmentPlan/treatmentPlan";
import AddNewUserForm from "../forms/addNewUserForm/addNewUserForm";
import QuestionReports from "../questionReports/questionReports";
import Dashboard from "../dashboards/dashboard/dashboard";
import ClinicSummary from "../clinicSummary/clinicSummary";
import { tWithKeyAttr as t } from "../../i18nHelper";
import GenerateTreatmentReportSummary from "../generateTreatmentReportsSummary/generateTreatmentReportSummary";
import ReportsSummary from "../reportsSummary/reportsSummary";
import UserExercisesTable from "../userExercisesTable/userExercisesTable";
import { useLazyGetUsersByCompanyIdWithRecommendsQuery } from "../../services/api/user.api";
import { canAddDefaultReportsSelector, adminCanViewPatientRecordingsSelector } from "../../services/selectors/auth.selectors";
import Tooltip from "../tooltip/tooltip";
import UserComments from "../userComments/userComments";
import UserRecordingsTable from "../userRecordingsTable/userRecordingsTable";
import UserDocuments from "../userDocuments/userDocuments";

function EditData({
  user,
  onClose,
  skipApiCallsOnClose = false,
  initialCategory,
  fromAppointments = false,
  activeFilters,
}: {
  user: IUserReport | undefined;
  onClose: () => void;
  skipApiCallsOnClose?: boolean;
  initialCategory?: string;
  fromAppointments?: boolean;
  activeFilters?: any;
}) {
  const mobile = useMediaQuery(mobileWidth);

  const admin = useAppSelector(adminSelector);
  const canAddDefaultReports = useAppSelector(canAddDefaultReportsSelector);
  const adminCanViewPatientRecordings = useAppSelector(adminCanViewPatientRecordingsSelector);

  const [getAllUsersByCompanyIdWithRecommends] = useLazyGetUsersByCompanyIdWithRecommendsQuery();

  const [category, setCategory] = useState<string | undefined>(initialCategory);
  const [updatedUser, setUpdatedUser] = useState<IUserReport | undefined>(user);
  const [hoveredActionReport, setHoveredActionReport] = useState(false);


  // Handle initial category when it changes
  useEffect(() => {
    if (initialCategory) {
      setCategory(initialCategory);
    }
  }, [initialCategory]);

  const handleClose = () => {
    // If we came from appointments and we're in a specific category, exit completely
    if (fromAppointments && category) {
      onClose();
      return;
    }
    
    // Otherwise, just clear the category (back to links view)
    setCategory(undefined);
    if (user && !skipApiCallsOnClose) {
      // Removed re-fetch on close as requested to avoid filter reset
    }
  };

  if (user && category === "actionsReport") {
    return <ActionReport user={user} onClose={handleClose} />;
  } else if (user && category === "treatmentPlan") {
    return <TreatmentPlan user={user} onClose={handleClose} />;
  } else if (user && category === "questionsReports") {
    return (
      <QuestionReports user={user} onClose={handleClose} />
    );
  } else if (user && category === "editPersonalData") {
    return (
      <AddNewUserForm
        // setOpenSuccessModal={setOpenSuccessModal}
        isStaff={user.Users.RoleId !== 1 && user.Users.roleId !== 1}
        onClose={handleClose}
        user={updatedUser} // שימוש ב-updatedUser במקום user
        onUserUpdated={() => {
          // Refresh user data after successful save using active filters
          if (user) {
            getAllUsersByCompanyIdWithRecommends(activeFilters || {
              companyIds: [user.Users.CompanyId],
              roleIds: [user.Users.RoleId !== 1 ? -1 : 1],
              fakeCompanyIds: [admin?.fakeCompanyId || -1],
            }).then((res) => {
              if ("data" in res && res.data?.Users) {
                // Find the updated user from the response
                const updatedUserData = res.data.Users.find(
                  (u: IUserReport) => u.Users.Id === user.Users.Id
                );
                if (updatedUserData) {
                  setUpdatedUser(updatedUserData);
                }
              }
            });
          }
        }}
        activeFilters={activeFilters}
      />
    );
  } else if (user && category === "dashboardReports") {
    return (
      <Dashboard
        usersId={user.Users.Id}
        onClose={handleClose}
      />
    );
  } else if (user && category === "ClinicSummary") {
    return <ClinicSummary user={user} onClose={handleClose} />;
  } else if (user && category === "generateTreatmentReportsSummary") {
    return (
      <GenerateTreatmentReportSummary
        user={user}
        onClose={handleClose}
      />
    );
  } else if (user && category === "reports") {
    return (
      <ReportsSummary user={user} onClose={handleClose} />
    );
  } else if (user && category === "exercises") {
    return <UserExercisesTable user={user} onClose={handleClose} />;
  } else if (user && category === "userComments") {
    return <UserComments user={user.Users} onClose={handleClose} />;
  } else if (user && category === "meetingRecordings") {
    return <UserRecordingsTable user={user} onClose={handleClose} />;
  } else if (user && category === "documents") {
    return <UserDocuments user={user} onClose={handleClose} />;
  }

  return (
    <article className={`menuSection`}>
      {!mobile && (
        <BackArrowButton
          text={t("PREVIOUS_SCREEN")}
          onClick={onClose}
          position={"right"}
        />
      )}
      <ul className={`options`}>
        {getUserActionsEdit().filter((a) => {
          if (a.value === "meetingRecordings" && !adminCanViewPatientRecordings) return false;
          return true;
        }).map((a, index) => {
          const isActionReportDisabled = a.value === "actionsReport" && !canAddDefaultReports;
          const isDisabled = (a.disabledForFakeCompanyId && admin.fakeCompanyId) || isActionReportDisabled;

          return (
            <li
              key={index}
              className={`option ${isDisabled ? "button_disabled" : "hover"}`}
              onClick={() => {
                if (isDisabled) return;
                setCategory(a.value);
              }}
              onMouseEnter={() => isActionReportDisabled && setHoveredActionReport(true)}
              onMouseLeave={() => isActionReportDisabled && setHoveredActionReport(false)}
              style={{ position: "relative" }}
            >
              {t(a.text)}
              {isActionReportDisabled && (
                <Tooltip isShown={hoveredActionReport} position="center">
                  {t("ACTION_REPORT_WITHOUT_APPOINTMENT_IS_DISABLED")}
                </Tooltip>
              )}
            </li>
          );
        })}
        {mobile && (
          <button onClick={onClose} className={`button ${styles.btn} hover`}>
            {t("PREVIOUS_SCREEN")}
          </button>
        )}
      </ul>
    </article>
  );
}

export default EditData;
